import React, { useState, useEffect, useCallback, useRef } from "react";
import { LOGIN } from "../../constants/constants";
import { initiateAuthorizationCodeLogin, checkAuthBackendHealth, handleAuthorizationCallback, checkAuthenticationStatus, verifyEmailAuthorization } from "../../services/auth-code-service";
import { getAuthToken, getCachedUser, clearUserSession } from "../../utils/auth-utils";
import "./login-page.scss";
import newImage from '../../assets/images/newimage.jpeg';
import maerskLogo from '../../assets/images/maersk-logo-text.png';

const LoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [backendHealthy, setBackendHealthy] = useState(true);
  const [authError, setAuthError] = useState('');
  const MAX_LOGIN_WAIT_MS = 15000; // 15s safety timeout
  const timeoutRef = useRef(null);

  // Clear loading state safely
  const stopLoading = useCallback(() => {
    setIsLoading(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Handle OAuth callback if code present in URL
  const maybeHandleCallback = useCallback(async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const error = params.get('error');
      if (code || error) {
        setIsLoading(true);
        const result = await handleAuthorizationCallback(code, state, error);
        if (result?.success) {
          // Verify the email returned from SSO before allowing access
          const email = result.userInfo?.email;
          if (email) {
            const { authorized, message } = await verifyEmailAuthorization(email);
            if (!authorized) {
              clearUserSession();
              sessionStorage.removeItem('justAuthenticated');
              stopLoading();
              setAuthError(message);
              window.history.replaceState({}, document.title, window.location.pathname);
              return true;
            }
          }
          stopLoading();
          // Remove query params to avoid re-processing on back navigation
          window.history.replaceState({}, document.title, window.location.pathname);
          // Redirect to app homepage after slight delay
          setTimeout(() => {
            window.location.href = '/';
          }, 400);
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('Callback handling failed:', err);
      stopLoading();
      return true; // We attempted handling
    }
  }, [stopLoading]);

  // On mount: check existing auth, backend health, and callback
  useEffect(() => {
    let mounted = true;
    (async () => {
      console.log('Preparing sign in...');
      // Health check
      try {
        const health = await checkAuthBackendHealth();
        if (!mounted) return;
        setBackendHealthy(health.healthy);
        if (!health.healthy) {
          stopLoading();
          return;
        }
      } catch (e) {
        console.warn('Health check error:', e);
      }

      // If callback parameters present, handle them
      const handled = await maybeHandleCallback();
      if (handled) return; // Already processing callback

      // Already authenticated?
      const token = getAuthToken();
      const cachedUser = getCachedUser();
      if (token && cachedUser) {
        console.log(`Welcome back ${cachedUser.name || ''}`);
        // Silent validation of token
        const authStatus = await checkAuthenticationStatus();
        if (authStatus.isAuthenticated) {
          // Verify email authorization for the existing session
          const email = cachedUser.email || authStatus.userInfo?.email;
          if (email) {
            const { authorized, message } = await verifyEmailAuthorization(email);
            if (!authorized) {
              clearUserSession();
              sessionStorage.removeItem('justAuthenticated');
              if (mounted) setAuthError(message);
              stopLoading();
              return;
            }
          }
          stopLoading();
          setTimeout(() => { window.location.href = '/'; }, 500);
          return;
        }
      }
      console.log('Ready to sign in');
    })();
    return () => { mounted = false; };
  }, [maybeHandleCallback, stopLoading]);

  // Listen for auth state changes (e.g., token stored in another component)
  useEffect(() => {
    const handler = () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('code')) return;
      const token = getAuthToken();
      if (token) {
        stopLoading();
        setTimeout(() => { window.location.href = '/'; }, 400);
      }
    };
    window.addEventListener('authStateChanged', handler);
    return () => window.removeEventListener('authStateChanged', handler);
  }, [stopLoading]);

  // Start a safety timeout when loading begins
  useEffect(() => {
    if (isLoading) {
      timeoutRef.current = setTimeout(() => {
        stopLoading();
      }, MAX_LOGIN_WAIT_MS);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isLoading, stopLoading]);

  const handleLogin = async () => {
    try {
      if (!backendHealthy) {
        console.log('Auth service down. Please try later.');
        return;
      }
      setAuthError('');
      setIsLoading(true);
      console.log('Starting authorization code flow...');
      await initiateAuthorizationCodeLogin();
      // Email verification happens in maybeHandleCallback after SSO redirects back
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
      const friendly = error.message || 'Login failed';
      console.log(friendly);
    }
  };

  return (
    <div
      className="login-background-container"
      style={{
        backgroundImage: `url(${newImage})`,
      }}
    >
      <div className="content-wrapper">
        <div className="logo-box">
          <img
            src={maerskLogo}
            alt="Maersk Logo"
            width={320}
            height={120}
          />
        </div>
        <div className="welcome-msg">{LOGIN.WELCOME_MSG}</div>
        <div className="welcome-msg-sub">{LOGIN.MORE_INFO}</div>
        {authError && (
          <div className="auth-error-msg">{authError}</div>
        )}
        <button
          onClick={handleLogin}
          disabled={isLoading}
          style={{
            padding: '12px 24px',
            backgroundColor: '#42B0D5',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: isLoading ? 'wait' : 'pointer',
            marginTop: '10px',
            fontFamily: 'Maersk Text, Arial, sans-serif',
            opacity: isLoading ? 0.7 : 1,
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => !isLoading && (e.target.style.backgroundColor = '#003E62')}
          onMouseOut={(e) => !isLoading && (e.target.style.backgroundColor = '#42B0D5')}
        >
          {isLoading ? "Signing In..." : LOGIN.LOGIN_BUTTON_NAME}
        </button>
      </div>
    </div>
  );
};

export default LoginPage;
